import { useEffect } from "react";

import { useConnectToSocket } from "@/components/connect-to-socket";
import { useSubscribeToBreakoutSessionChanges } from "@/features/breakout-sessions/api/subscribe-to-breakout-session-changes";
import { getSessionByKeyQueryKey } from "@/features/session/api/get-session-by-key";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { queryClient } from "@/lib/react-query";
import socketClient from "@/lib/socket-io";
import {
  CallStatusPayload,
  PublicSession,
  SessionByRoomKey,
} from "@classview/shared-types";

import { isRoomSystem } from "../utils/participants-utils";

/**
 * This hook is used to subscribe to the participants in the call.
 * It will update the moderation status and the moderator ids.
 * It will also update if participant can screen share their screen
 */
export function useSubscribeToParticipants() {
  const sessionId = useVidyoStore((state) => state.sessionId);
  const pin = useVidyoStore((state) => state.pin);
  const isVidyoConnected = useVidyoStore((state) => state.isVidyoConnected);

  const updateParticipantsStatus = useVidyoStore(
    (state) => state.participants.updateParticipantsStatus,
  );
  const setModeratorIds = useVidyoStore((state) => state.setModeratorIds);
  const resetCall = useVidyoStore((state) => state.resetCall);
  const setIsParticipantScreenSharing = useVidyoStore(
    (state) => state.screenShare.setIsParticipantScreenSharing,
  );
  const setIsChatEnabled = useVidyoStore(
    (state) => state.chat.setIsChatEnabled,
  );
  const setIsRaisedHandSoundEnabled = useVidyoStore(
    (state) => state.participants.setIsRaisedHandSoundEnabled,
  );
  const isWebSocketAuthenticated = useVidyoStore(
    (state) => state.webSocket.isAuthenticated,
  );

  const { isSocketConnected } = useConnectToSocket();
  const enabled =
    isSocketConnected &&
    sessionId !== "" &&
    isVidyoConnected &&
    isWebSocketAuthenticated;

  useSubscribeToBreakoutSessionChanges({
    enabled: sessionId !== "" && isVidyoConnected,
    socketClient,
  });

  /**
   * This is used to handle the call status events of the socket client
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    socketClient.emit("joinCall", {
      sessionId,
      pin: pin || undefined,
    });
    console.log("[useSubscribeToParticipants] joined call");

    const handleCallStatus = (data: CallStatusPayload) => {
      setModeratorIds(data.moderatorIds);
      updateParticipantsStatus(
        data.muteStatuses.map((p) => {
          return {
            participantId: p.participantID,
            entityId: p.entityId,
            name: p.displayName,
            isAudioEnabled: p.isAudioEnabled,
            isVideoEnabled: p.isVideoEnabled,
            isRoomSystem: isRoomSystem(p),
            ...(p.isHandRaised
              ? {
                  isHandRaised: true as const,
                  handRaisedAt: p.handRaisedAt
                    ? new Date(p.handRaisedAt)
                    : new Date(),
                }
              : { isHandRaised: false as const }),
          };
        }),
      );
      setIsParticipantScreenSharing(data.isScreenSharingEnabled);
      setIsChatEnabled(data.isChatEnabled);
      setIsRaisedHandSoundEnabled(data.isRaisedHandSoundEnabled);
    };

    const handleSessionUpdated = (session: PublicSession) => {
      queryClient.setQueryData<SessionByRoomKey>(
        getSessionByKeyQueryKey({ key: session.room.key }),
        (oldSession) => {
          if (!oldSession) {
            return {
              id: session.id,
              name: session.name,
              description: session.description || "",
              start: session.start,
              end: session.end,
              //TODO: this already has changed to check if the user is an attendee
              canJoin: !!session.room.pin,
              pin: session.room.pin.toString(),
              parentSession: {
                id: session.parentSessionId || "",
                roomKey: session.room.key,
              },
              isCCEnabled: session.isCCEnabled,
            };
          }

          return {
            ...oldSession,
            name: session.name,
            description: session.description || "",
            start: session.start,
            end: session.end,
          };
        },
      );
    };

    socketClient.on("callStatus", handleCallStatus);

    socketClient.on("sessionUpdated", handleSessionUpdated);

    /**
     * The cleanup function is called when the component is unmounted
     * or when the dependencies change. (for example when the sessionId changes)
     */
    return () => {
      console.log("[useSubscribeToParticipants] disconnecting");
      socketClient.off("callStatus", handleCallStatus);
      socketClient.off("sessionUpdated", handleSessionUpdated);
      resetCall();
      console.log("call reset");
    };
  }, [
    sessionId,
    pin,
    updateParticipantsStatus,
    setModeratorIds,
    resetCall,
    setIsParticipantScreenSharing,
    setIsChatEnabled,
    setIsRaisedHandSoundEnabled,
    enabled,
    isSocketConnected,
  ]);
}
